import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './OverallPerformanceGraph.css';
import { useDispatch, useSelector } from 'react-redux';
import { left_click } from '../../assets/images/images';
import { getAiFeedbackPointByTeacher } from '../../state/actions/getAiFeedbackPointByTeacherAction'
import { useAuth } from '../../features/auth/AuthContext';

const OverallPerformanceGraph = ({ selectedCourseData, selectedStudentData, periodTab, year, month, startdate, enddate, monthYear , selectstudent, setdot, isAIGenerating }) => {
  
  const currentYear = new Date().getFullYear();

  const rawChartData = useSelector((state) => state?.getOverallPerformanceScoreByTeacher?.students?.data?.chart_data || [])
  const overall_average = useSelector((state) => state?.getOverallPerformanceScoreByTeacher?.students?.data?.overall_average || 0)
 
  const datas = useSelector((state) => state?.getOverallPerformanceScoreByTeacher || [])
  const dispatch = useDispatch();
  const { token } = useAuth();
  const aiFeedbackData = useSelector(state => state.getAiFeedbackPointByTeacher);

  const time_period = useSelector((state) => state.getOverallPerformanceScoreByTeacher?.students?.data?.time_period)
  const timePeriodString = String(time_period);

  // STALE-DATA GUARD:
  // This component does not fetch - it reads whatever response is currently in
  // the Redux store. During a tab switch (e.g. Monthly -> Yearly) the store
  // still holds the PREVIOUS tab's response until the new request resolves.
  // Rendering that stale data caused:
  //   1) a flash of the old tab's series for a few hundred ms
  //   2) ensureCompleteYearData() appending Jan-Dec placeholders NEXT TO the
  //      leftover July daily points -> the "mixed" yearly chart
  // The API response includes time_period, so we only trust chart_data when it
  // matches the tab currently selected. Otherwise we treat it as "loading".
  const isDataFresh = timePeriodString === periodTab;
  const yearlydata = isDataFresh ? rawChartData : [];

  const role = useSelector((state) => state?.globalState?.role || "");
  const student_id = useSelector((state) => state?.globalState?.student_details?.data?.student_id || "");

  // Function to get the last day of a month
  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Function to get month name by number
  const getMonthName = (monthNumber) => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return monthNames[monthNumber - 1];
  };

  // Function to format month period with correct last date
  const formatMonthPeriod = (period, effectiveYear, effectiveMonth) => {
    if (!period || typeof period !== 'string') {
      return period;
    }

    // Check if this is a month tab and period follows the pattern like "29 May"
    if (periodTab === 'month' && period.includes(' ')) {
      const parts = period.split(' ');
      if (parts.length === 2) {
        const [day, monthName] = parts;
        
        // Check if this day is 29 (the fixed weekly interval)
        if (day === '29') {
          // Get the actual last day of the selected month
          const lastDay = getLastDayOfMonth(effectiveYear, effectiveMonth);
          return `${lastDay} ${monthName}`;
        }
      }
    }
    
    return period;
  };

  // Function to ensure all 5 weekly intervals are present for month view
  const ensureCompleteMonthData = (data, effectiveYear, effectiveMonth) => {
    if (periodTab !== 'month') {
      return data;
    }

    // Expected intervals for month view
    const expectedIntervals = [1, 8, 15, 22, 29];
    const currentMonthName = getMonthName(effectiveMonth);
    const lastDayOfMonth = getLastDayOfMonth(effectiveYear, effectiveMonth);

    // Extract existing intervals from data
    const existingIntervals = new Set();
    data.forEach(item => {
      if (item.originalPeriod && item.originalPeriod.includes(' ')) {
        const day = parseInt(item.originalPeriod.split(' ')[0]);
        existingIntervals.add(day);
      }
    });

    // Add missing intervals with 0 scores
    expectedIntervals.forEach(expectedDay => {
      if (!existingIntervals.has(expectedDay)) {
        // Create the original period string (always use the expected day)
        const originalPeriod = `${expectedDay} ${currentMonthName}`;
        
        // Create the display period (show actual last day for 29th interval)
        const displayPeriod = expectedDay === 29 ? 
          `${lastDayOfMonth} ${currentMonthName}` : 
          originalPeriod;
        
        data.push({
          month: displayPeriod,
          originalPeriod: originalPeriod,
          full_period: originalPeriod, // Include full_period for consistency
          submission_count: 0,
          score: 0,
          isPlaceholder: true // Mark as placeholder for potential styling
        });
      }
    });

    // Sort data by day to maintain proper order
    data.sort((a, b) => {
      const getDayFromPeriod = (item) => {
        const period = item.originalPeriod || item.month;
        if (period && period.includes(' ')) {
          return parseInt(period.split(' ')[0]);
        }
        return 0;
      };
      
      return getDayFromPeriod(a) - getDayFromPeriod(b);
    });

    return data;
  };

  // Function to ensure all 12 months are present for year view
  const ensureCompleteYearData = (data, effectiveYear) => {
    if (periodTab !== 'year') {
      return data;
    }

    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const fullMonthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // SAFETY FILTER: only keep entries whose period is actually a month name.
    // If any non-month entries (e.g. "2 Jul" daily points from a stale monthly
    // response) ever slip through, drop them instead of appending 12 month
    // placeholders alongside them.
    data = data.filter(item => {
      const period = item.originalPeriod || item.month;
      return monthNames.includes(period);
    });

    // Extract existing months from data
    const existingMonths = new Set();
    data.forEach(item => {
      if (item.originalPeriod || item.month) {
        const period = item.originalPeriod || item.month;
        existingMonths.add(period);
      }
    });

    // Add missing months with 0 scores
    monthNames.forEach((monthName, index) => {
      if (!existingMonths.has(monthName)) {
        data.push({
          month: monthName,
          originalPeriod: monthName,
          full_period: fullMonthNames[index], // Include full month name
          submission_count: 0,
          score: 0,
          isPlaceholder: true
        });
      }
    });

    // Sort data by month order
    data.sort((a, b) => {
      const getMonthIndex = (item) => {
        const period = item.originalPeriod || item.month;
        return monthNames.indexOf(period);
      };
      
      return getMonthIndex(a) - getMonthIndex(b);
    });

    return data;
  };

  // Custom tick component for multi-line text in weekly view
  const CustomTick = (props) => {
    const { x, y, payload } = props;
    const value = payload.value;
    
    // For week tab, we need to get the day name from the month value
    if (periodTab === 'week') {
      const monthValue = value; // This is the month string like "15 May"
      
      if (monthValue && typeof monthValue === 'string' && monthValue.includes(' ')) {
        const [day, monthName] = monthValue.split(' ');
        
        // Create a date to get the day name
        const monthNumber = new Date(`${monthName} 1, 2021`).getMonth(); // Get month index
        const effectiveYear = year || new Date().getFullYear();
        const date = new Date(effectiveYear, monthNumber, parseInt(day));
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        
        return (
          <g transform={`translate(${x},${y})`}>
            <text 
              x={0} 
              y={0} 
              dy={16} 
              textAnchor="middle" 
              fill="#666" 
              fontSize="12"
            >
              <tspan x={0} dy="0">{monthValue}</tspan>
              <tspan x={0} dy="16">{dayName}</tspan>
            </text>
          </g>
        );
      }
    }
    
    // Fallback for other cases
    return (
      <g transform={`translate(${x},${y})`}>
        <text 
          x={0} 
          y={0} 
          dy={16} 
          textAnchor="middle" 
          fill="#666" 
          fontSize="12"
        >
          <tspan x={0} dy="0">{value}</tspan>
        </text>
      </g>
    );
  };

  // TAB-AWARE PLACEHOLDER:
  // When there is no fresh data yet (empty response, or stale data blanked by
  // the isDataFresh guard during a tab switch), render an empty axis that
  // matches the CURRENT tab instead of the old static Jan-Dec list. The old
  // list, sliced by the 6-item window, is what flashed "Jan - Jun" on the
  // monthly/weekly/custom tabs.
  const getPlaceholderData = () => {
    const effectiveYear = year || new Date().getFullYear();
    const effectiveMonth = parseInt(month, 10) || new Date().getMonth() + 1;
    const monthName = getMonthName(effectiveMonth);

    if (periodTab === 'month') {
      // Same 5 weekly intervals ensureCompleteMonthData produces
      const lastDay = getLastDayOfMonth(effectiveYear, effectiveMonth);
      return [1, 8, 15, 22, 29].map((d) => ({
        month: d === 29 ? `${lastDay} ${monthName}` : `${d} ${monthName}`,
        originalPeriod: `${d} ${monthName}`,
        score: 0,
        submission_count: 0,
        isPlaceholder: true,
      }));
    }

    if (periodTab === 'week') {
      // First 7 days of the selected month, matching the "D MMM" tick format
      return [1, 2, 3, 4, 5, 6, 7].map((d) => ({
        month: `${d} ${monthName}`,
        originalPeriod: `${d} ${monthName}`,
        score: 0,
        submission_count: 0,
        isPlaceholder: true,
      }));
    }

    if (periodTab === 'custom') {
      // Build the axis from the selected DD-MM-YYYY range (up to 6 points)
      const parse = (s) => {
        if (!s || !/^\d{2}-\d{2}-\d{4}$/.test(s)) return null;
        const [dd, mm, yyyy] = s.split('-').map(Number);
        return new Date(yyyy, mm - 1, dd);
      };
      const start = parse(startdate);
      const end = parse(enddate);
      if (start && end && end >= start) {
        const points = [];
        const totalDays = Math.round((end - start) / 86400000) + 1;
        const count = Math.min(totalDays, 6);
        for (let i = 0; i < count; i++) {
          const d = new Date(start.getTime() + Math.round((i * (totalDays - 1)) / Math.max(count - 1, 1)) * 86400000);
          points.push({
            month: `${d.getDate()} ${getMonthName(d.getMonth() + 1)}`,
            originalPeriod: `${d.getDate()} ${getMonthName(d.getMonth() + 1)}`,
            score: 0,
            submission_count: 0,
            isPlaceholder: true,
          });
        }
        return points;
      }
      // No valid range yet - render an empty chart rather than wrong labels
      return [];
    }

    // year tab: full 12-month axis (unchanged behavior)
    return [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ].map((m) => ({
      month: m,
      originalPeriod: m,
      score: 0,
      submission_count: 0,
      isPlaceholder: true,
    }));
  };

  // UPDATED allData transformation with complete month intervals and year support
  const allData = yearlydata.length > 0 ? (() => {
    const effectiveYear = year || new Date().getFullYear();
    const effectiveMonth = month || new Date().getMonth() + 1; // 1-based month
    
    // First transform the API data
    let transformedData = yearlydata.map((item) => {
      const formattedPeriod = formatMonthPeriod(item.period, effectiveYear, effectiveMonth);
      
      return {
        month: formattedPeriod,
        originalPeriod: item.period,
        full_period: item.full_period || item.period, // Include full_period from API response
        submission_count: item.submission_count || 0,
        score: item.average_score || 0,
        isPlaceholder: false
      };
    });

    // Ensure all intervals are present based on period tab
    if (periodTab === 'month') {
      transformedData = ensureCompleteMonthData(transformedData, effectiveYear, effectiveMonth);
    } else if (periodTab === 'year') {
      transformedData = ensureCompleteYearData(transformedData, effectiveYear);
    }
    
    return transformedData;
  })() : getPlaceholderData();

  // State to track current view window
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const windowSize = periodTab === 'week' ? 7 : periodTab === 'year' ? 12 : 6; // CHANGED: 12 for year view

  useEffect(() => {
    setCurrentWeekIndex(0);
    setCurrentDayIndex(0);
  }, [periodTab]);

  // Calculate total weeks and total days based on data length
  const totalWeeks = Math.ceil(allData.length / windowSize);
  const totalDays = allData.length;

  // Get current visible data based on periodTab
  const data = (() => {
    if (periodTab === 'week') {
      return allData.slice(currentWeekIndex * windowSize, (currentWeekIndex * windowSize) + windowSize);
    } else if (periodTab === 'year') {
      // CHANGED: Show all 12 months for year view
      return allData;
    } else {
      // For month and other views
      return allData.slice(currentDayIndex, currentDayIndex + windowSize);
    }
  })();

  // Navigation functions - different behavior based on periodTab
  const goToPrevious = () => {
    if (periodTab === 'week') {
      if (currentWeekIndex > 0) {
        setCurrentWeekIndex(currentWeekIndex - 1);
      }
    } else {
      if (currentDayIndex > 0) {
        setCurrentDayIndex(currentDayIndex - 1);
      }
    }
  };
  
  const goToNext = () => {
    if (periodTab === 'week') {
      if (currentWeekIndex < totalWeeks - 1) {
        setCurrentWeekIndex(currentWeekIndex + 1);
      }
    } else {
      if (currentDayIndex + windowSize < totalDays) {
        setCurrentDayIndex(currentDayIndex + 1);
      }
    }
  };

  // CHANGED: Disable navigation for year view
  const canGoPrevious = periodTab === 'year' ? false : (periodTab === 'week' 
    ? currentWeekIndex > 0 
    : currentDayIndex > 0);
    
  const canGoNext = periodTab === 'year' ? false : (periodTab === 'week'
    ? currentWeekIndex < totalWeeks - 1
    : currentDayIndex + windowSize < totalDays);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1a202c',
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '12px',
          width: '235px',
        }}>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
        }}>
        <p style={{fontSize: "12px", margin: 0, color: '#ffffff', borderRight: "1px solid #ffffff", fontWeight:"400", paddingRight:"5px"}}>{label}</p>
         <div style={{ 
            color: '#ffffff',
            fontSize: '12px',
            fontWeight:"400",
            padding: '5px 5px 5px 7px',
             borderRadius: '6px'
           }}>
            
            <span><img src={left_click} alt='left-click' style={{marginRight:"5px"}}/></span>
            Click to see AI Feedback
          </div>
        </div>
          
          <div style={{
            display:"flex",
            alignItems:"center",
          }}>
            {/* Colored dot */}
           <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor:"#4A90E2",
                marginRight: '10px',
                flexShrink: 0
            }}></div>
            <p style={{ margin: 0, color: '#ffffff', fontWeight:"500", fontSize: "13px", paddingTop:"5px", }}> Avg. Student's Score: {`${payload[0].value}%`}</p>
          </div>
            
        </div>
      );
    }
    return null;
  };

  // UPDATED handleDotClick to work with originalPeriod and handle placeholders
  const handleDotClick = (data, index) => {
    // Don't trigger API call for placeholder data with 0 scores
    if (data?.isPlaceholder || data?.submission_count === 0) {
      return;
    }

    const currentYear = new Date().getFullYear();
    const effectiveYear = year || currentYear;
    const students = selectedStudentData;
    const selectedcourselist =  role === "Teacher"? selectedCourseData : selectstudent;
    const time_period = periodTab;
    const avg_score = data?.score;

    let params = {};
    let month_year, week_start_date, daily_date, start_date, end_date;
    let day, monthName, monthNumber;

    // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
    // For placeholder dots, use originalPeriod as it contains the expected interval
    let periodToProcess;
    if (data?.isPlaceholder) {
      // For placeholders, use originalPeriod which contains the expected interval
      periodToProcess = data?.originalPeriod || data?.month;
    } else {
      // For real data dots, prioritize month field which contains the actual date
      periodToProcess = data?.month || data?.originalPeriod;
    }

    // Ensure period exists and is valid
    if (periodToProcess) {
      const period = periodToProcess;
      const periodParts = period.split(" ");

      if (periodParts.length === 2) {
        [day, monthName] = periodParts;
        monthNumber = new Date(`${monthName} 1, 2021`).getMonth() + 1;
        daily_date = `${day.padStart(2, '0')}-${monthNumber.toString().padStart(2, '0')}-${effectiveYear}`;
      } else if (periodParts.length === 1) {
        monthName = periodParts[0];
        monthNumber = new Date(`${monthName} 1, 2021`).getMonth() + 1;
        daily_date = `01-${monthNumber.toString().padStart(2, '0')}-${effectiveYear}`;
      } else {
        console.error(`Invalid period format: ${period}`);
        return;
      }
    } else {
      console.error("Period is undefined or null");
      return;
    }

    // Set the correct params for the API call
    if (time_period === 'year') {
      // Derive month_year from dot's data instead of using calendar selection
      // monthName is already extracted from the dot's period data
      month_year = `${monthName}-${effectiveYear}`;
    } else if (time_period === 'month') {
      week_start_date = `${day}-${monthNumber}-${effectiveYear}`;
      params.week_start_date = week_start_date;
      delete params.daily_date;
    } else if (time_period === 'week') {
      daily_date = `${day}-${monthNumber}-${effectiveYear}`;
      params.daily_date = daily_date;
    } else if (time_period === 'custom') {
      start_date = startdate;
      end_date = enddate;
      params.start_date = start_date;
      params.end_date = end_date;
    }

    if (role === "Student") {
      params.student_id = student_id;
    } else {
      params.students = students.join(',');
    }

    // Set the dot data with full_period field for proper title display
    const dotDataWithFullPeriod = {
      ...data,
      full_period: data.full_period || data.period || data.month,
      originalPeriod: data.originalPeriod || data.period || data.month,
      score: data.score || data.average_score,
      submission_count: data.submission_count,
      // Add the actual date/period information for the title
      // Use the calculated daily_date (DD-MM-YYYY format) instead of periodToProcess
      actualDate: daily_date,
      actualMonth: monthName,
      actualYear: effectiveYear
    };
    
    // Set the dot data so AIFeedbackPerformance can use the full_period
    if (setdot) {
      setdot(dotDataWithFullPeriod);
    }

    // Dispatch the action to fetch data
    dispatch(getAiFeedbackPointByTeacher(
      token,
      students,
      selectedcourselist,
      time_period,
      month_year,
      avg_score,
      week_start_date,
      daily_date,
      start_date,
      end_date,
      role,
      student_id,
      overall_average
    ));
  };

  // Custom dot component with conditional rendering for placeholders
  const CustomDot = (props) => {
    const { cx, cy, payload, index } = props;

    // Different styling for placeholder data
    const isPlaceholder = payload?.payload?.isPlaceholder || payload?.payload?.submission_count === 0;
    
    return (
      <g style={{ cursor: isPlaceholder ? 'default' : 'pointer' }}>
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={isPlaceholder ? "#ccc" : "#4A90E2"}
          stroke="white"
          strokeWidth={2}
          style={{ cursor: isPlaceholder ? 'default' : 'pointer' }}
          opacity={isPlaceholder ? 0.5 : 1}
        />
        {/* Only add click area for non-placeholder data */}
        {!isPlaceholder && (
          <circle
            cx={cx}
            cy={cy}
            r={10}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleDotClick(payload, index);
            }}
          />
        )}
      </g>
    );
  };

  return (
    <div 
      className='overall-performance-Ggraph-section'
      style={{ 
        opacity: isAIGenerating ? 0.5 : 1, 
        pointerEvents: isAIGenerating ? 'none' : 'auto' 
      }}
    >
      <div className='overall-performance-Ggraph'>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{
              top: 30,
              right: 20,
              left: -10,
              bottom: periodTab === 'week' ? 60 : 40,
            }}
          >
            <CartesianGrid
              strokeDasharray="none"
              stroke="#f0f0f0"
              horizontal={true}
              vertical={false}
            />

            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              tick={periodTab === 'week' ? (props) => <CustomTick {...props} /> : { fontSize: 12, fill: '#666' }}
              dy={10}
            />

            <YAxis
              domain={[0, 100]}
              axisLine={{ stroke: '#D3D3D3', strokeWidth: 1 }}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={(value) => `${value}%`}
              ticks={[0, 25, 50, 75, 100]}
            />

            <Tooltip content={<CustomTooltip />} />

            <defs>
              <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4A90E2" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#4A90E2" stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <Area
              type="linear"
              dataKey="score"
              stroke="#4A90E2"
              strokeWidth={2}
              strokeDasharray="5 5"
              fill="url(#colorScore)"
              dot={<CustomDot />}
              activeDot={{
                r: 6,
                fill: '#4A90E2',
                stroke: '#0068FF4A',
                strokeWidth: 12,
                style: { cursor: 'pointer' },
                onClick: (e, payload) => {
                  console.log("Active dot clicked", payload);
                  if(payload?.payload?.submission_count > 0 && !payload?.payload?.isPlaceholder) {
                     handleDotClick(payload?.payload, payload?.index);
                  }
                }
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className='overall-performance-Ggraph-navigation'>
        <button
          onClick={goToPrevious}
          disabled={!canGoPrevious}
          style={{
            background: 'none',
            border: 'none',
            cursor: canGoPrevious ? 'pointer' : 'not-allowed',
            fontSize: '20px',
            color: canGoPrevious ? '#666' : '#ccc',
            padding: '4px 8px',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (canGoPrevious) e.target.style.color = '#4A90E2';
          }}
          onMouseLeave={(e) => {
            if (canGoPrevious) e.target.style.color = '#666';
          }}
        >
          &#8249;
        </button>

        <div style={{
          fontSize: '12px',
          fontWeight:"bold",
          color: '#000000',
          alignSelf: 'center',
          userSelect: 'none',
        }}>
          {data.length > 0 && (
            periodTab === 'week' 
              ? (() => {
                  const firstDay = parseInt(data[0].month.split(' ')[0]);
                  const weekNumber = Math.ceil(firstDay / 7);
                  return `Week ${weekNumber}`;
                })()
              : periodTab === 'year'
              ? 'Months' // CHANGED: Updated label for year view
              : periodTab === 'month'
              ? monthYear
              : periodTab === 'custom'
              ? 'Custom Date Range'
              : ''
          )}
        </div>

        <button
          onClick={goToNext}
          disabled={!canGoNext}
          style={{
            background: 'none',
            border: 'none',
            cursor: canGoNext ? 'pointer' : 'not-allowed',
            fontSize: '20px',
            color: canGoNext ? '#666' : '#ccc',
            padding: '4px 8px',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (canGoNext) e.target.style.color = '#4A90E2';
          }}
          onMouseLeave={(e) => {
            if (canGoNext) e.target.style.color = '#666';
          }}
        >
          &#8250;
        </button>
      </div>
    </div>
  );
};

export default OverallPerformanceGraph;